import type {
  BuiltPlan,
  Category,
  NeglectedRegion,
  Outing,
  OutingSummary,
  Person,
  Plan,
  PlanSummary,
  PlannerConstraints,
  Region,
  Season,
  Suggestion,
  Visit,
} from '@ambit/shared';

/** What `GET /outings/:id` adds on top of the stored record. */
export interface OutingDetail extends Outing {
  regionName: string;
  pairs: { id: string; name: string; category: Category; note: string | null }[];
  visitHistory: { visitId: string; visitedOn: string; rating: number | null }[];
}

/** Server responses are always `{ data, meta? }`; errors `{ error }`. */
interface Envelope<T> {
  data: T;
  meta?: { total: number; limit: number; offset: number };
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly hint?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<Envelope<T>> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = body?.error ?? {};
    throw new ApiError(
      error.code ?? 'unknown',
      error.message ?? `Request failed (${response.status})`,
      error.hint,
      response.status,
    );
  }
  return body as Envelope<T>;
}

export interface AdminHousehold {
  id: string;
  name: string;
  tokenVersion: number;
  partySize: number | null;
  homeLat: number | null;
  homeLon: number | null;
  /** Null until a feed link has been asked for. */
  calendarToken: string | null;
}

export const api = {
  async authStatus() {
    const { data } = await request<{
      authenticated: boolean;
      kind: 'household' | 'identity' | null;
      name: string | null;
      email: string | null;
      householdName: string | null;
      /** Null when the household has not said. See CATALOG_PARTY_SIZE. */
      partySize: number | null;
      publicRead: boolean;
      oidcAvailable: boolean;
    }>('/auth/me');
    return data;
  },

  async login(passphrase: string) {
    await request('/auth', { method: 'POST', body: JSON.stringify({ passphrase }) });
  },

  async logout() {
    await request('/auth', { method: 'DELETE' });
  },

  async listOutings(params: {
    category?: Category;
    region?: string;
    maxDrive?: number;
    maxCost?: number;
    season?: Season | 'any';
    sort?: 'nearest' | 'substance' | 'name';
    q?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '' && value !== 'any') query.set(key, String(value));
    }
    return request<OutingSummary[]>(`/outings?${query}`);
  },

  /**
   * The full record — everything the catalog knows, including the fields the
   * grid has no room for. Fetched on demand rather than folded into the list
   * response: the detail is wanted for one card at a time, and inlining it
   * would put 176 copies of prose behind a screen that shows two lines of it.
   */
  async getOuting(id: string) {
    const { data } = await request<OutingDetail>(`/outings/${encodeURIComponent(id)}`);
    return data;
  },

  async listRegions() {
    const { data } = await request<Region[]>('/regions');
    return data;
  },

  async buildPlan(heroId: string, constraints: Partial<PlannerConstraints>, seed?: string) {
    const { data } = await request<BuiltPlan>('/plans/build', {
      method: 'POST',
      body: JSON.stringify({ heroId, constraints, seed }),
    });
    return data;
  },

  async rerollSlot(args: {
    heroId: string;
    slot: 'food' | 'addon';
    currentId?: string;
    keepIds: string[];
    constraints: Partial<PlannerConstraints>;
  }) {
    const { data } = await request<BuiltPlan>('/plans/reroll', {
      method: 'POST',
      body: JSON.stringify(args),
    });
    return data;
  },

  async savePlan(plan: BuiltPlan, createdBy: string | null) {
    const { data } = await request<{ id: string }>('/plans', {
      method: 'POST',
      body: JSON.stringify({
        title: plan.title,
        note: plan.notes.join(' ') || null,
        seasonHint: plan.constraints.season === 'any' ? [] : [plan.constraints.season],
        createdBy,
        generatorSeed: plan.seed,
        stops: plan.stops.map((s) => ({ outingId: s.outing.id, slot: s.slot })),
      }),
    });
    return data;
  },

  async listPlans(options: { curated?: boolean; sort?: 'votes' | 'recent' } = {}) {
    const query = new URLSearchParams();
    if (options.curated !== undefined) query.set('curated', String(options.curated));
    if (options.sort) query.set('sort', options.sort);
    const { data } = await request<PlanSummary[]>(`/plans?${query}`);
    return data;
  },

  async getPlan(id: string) {
    const { data } = await request<Plan>(`/plans/${id}`);
    return data;
  },

  /** Null clears the date, turning a booked day back into a candidate. */
  async schedulePlan(id: string, scheduledOn: string | null) {
    const { data } = await request<PlanSummary>(`/plans/${id}/schedule`, {
      method: 'PATCH',
      body: JSON.stringify({ scheduledOn }),
    });
    return data;
  },

  async archivePlan(id: string) {
    await request(`/plans/${id}`, { method: 'DELETE' });
  },

  // ── admin ──────────────────────────────────────────────────────────
  // Only an identity session reaches these; a household session gets 404 from
  // requireIdentity, which is deliberate — "not yours" and "does not exist"
  // should look the same from outside.

  async listHouseholds() {
    const { data } = await request<AdminHousehold[]>('/admin/households');
    return data;
  },

  async suggestHouseholdCode() {
    const { data } = await request<{ code: string }>('/admin/households/suggest-code');
    return data.code;
  },

  async createHousehold(input: {
    id: string;
    name: string;
    code?: string;
    partySize?: number | null;
    homeLat?: number | null;
    homeLon?: number | null;
  }) {
    const { data } = await request<{ household: AdminHousehold; code: string }>('/admin/households', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data;
  },

  async updateHousehold(
    id: string,
    patch: {
      name?: string;
      partySize?: number | null;
      homeLat?: number | null;
      homeLon?: number | null;
    },
  ) {
    const { data } = await request<AdminHousehold>(`/admin/households/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return data;
  },

  /**
   * Mint a feed link, or replace one. Unlike the household code this is
   * readable afterwards, so it can be shown again rather than only once.
   */
  async calendarToken(id: string, rotate = false) {
    const { data } = await request<{ token: string }>(`/admin/households/${id}/calendar`, {
      method: 'POST',
      body: JSON.stringify({ rotate }),
    });
    return data.token;
  },

  /** Returns the new code once. It is stored hashed and cannot be read back. */
  async rotateHouseholdCode(id: string, code?: string) {
    const { data } = await request<{ household: AdminHousehold; code: string }>(
      `/admin/households/${id}/rotate`,
      { method: 'POST', body: JSON.stringify(code ? { code } : {}) },
    );
    return data;
  },

  async listPeople() {
    const { data } = await request<Person[]>('/people');
    return data;
  },

  async createPerson(input: { name: string; isAdult: boolean; color?: string | null }) {
    const { data } = await request<Person>('/people', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data;
  },

  /** Idempotent: tapping an existing vote twice is not an error. */
  async vote(planId: string, personId: string) {
    await request(`/plans/${planId}/vote`, { method: 'PUT', body: JSON.stringify({ personId }) });
  },

  async unvote(planId: string, personId: string) {
    await request(`/plans/${planId}/vote`, { method: 'DELETE', body: JSON.stringify({ personId }) });
  },

  async listSuggestions(includeResolved = true) {
    const { data } = await request<Suggestion[]>(`/suggestions?includeResolved=${includeResolved}`);
    return data;
  },

  /**
   * Declining is the only way an idea leaves the queue, and the reason is not
   * optional on the server. "We tried this and it was shut" is worth keeping;
   * a silently vanished suggestion just gets suggested again.
   */
  async declineSuggestion(id: string, note: string) {
    const { data } = await request<Suggestion>(`/suggestions/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
    return data;
  },

  async createSuggestion(input: {
    what: string;
    why: string | null;
    url: string | null;
    suggestedBy: string | null;
  }) {
    const { data } = await request<Suggestion>('/suggestions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data;
  },

  async listVisits() {
    const { data } = await request<Visit[]>('/visits');
    return data;
  },

  async listNeglectedRegions(limit = 3) {
    const { data } = await request<NeglectedRegion[]>(`/stats/neglected?limit=${limit}`);
    return data;
  },

  async recordVisit(input: {
    planId: string | null;
    outingIds: string[];
    visitedOn: string;
    rating: number | null;
    notes: string | null;
    attendees: string[];
  }) {
    const { data } = await request<Visit>('/visits', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data;
  },
};
